import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Select, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { useMarketReportPrint, MarketReportMenu } from './MarketReportPrint';
import {
  appModalCloseButtonSx, appModalHeaderDividerSx, appModalPaperSx, appModalTitleBarSx, appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { leadsSelectMenuProps } from '../../Leads/components/leadsUi.styles';
import { metaPeriodSelectSx } from '../../MetaAds/components/metaAdsUi.styles';
import { colors } from '../../styles/theme';
import { DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import {
  MARKET_MONTHS_OPTIONS,
  MARKET_RADIUS_OPTIONS,
  MARKET_RENT_MAX_MONTHS,
} from '../../services/marketComparisonService';
import { useMarketComparison } from '../hooks/useMarketComparison';
import {
  MARKET_OPERATION_LABELS,
  formatMarketNumber,
  formatMarketRadius,
  describeLowSample,
  pickMarketBlock,
} from '../marketComparison';
import MarketSummary, { MarketSummarySkeleton } from './MarketSummary';
import MarketComparablesList, { MarketPhotoViewer } from './MarketComparablesList';
import {
  MARKET_FONT, MarketNotice, marketOutlineButtonSx, marketSmallSx,
} from './MarketParts';

export const MARKET_DIALOG_TITLE = 'Comparación de mercado';
/** Valores por defecto de la API: elegirlos de nuevo no cambia la clave de caché. */
export const MARKET_DEFAULT_RADIUS_M = 800;
export const MARKET_DEFAULT_MONTHS = 24;

const subtitleSx = { mt: 0.5, fontSize: '13px', lineHeight: 1.4, color: 'rgba(255,255,255,0.6)', fontFamily: MARKET_FONT };
/**
 * Alturas de la cabecera (ajuste 30 oct): fila de pestañas y filtros de 44 px con selects de
 * 28 px (el alto de los botones del informe, `marketLinkButtonSx`) → 8 px arriba y abajo; la
 * línea de Comparables, también 44 px con «Ver en el mapa» de 28 px centrado.
 */
export const MARKET_TOOLBAR_HEIGHT = 44;
export const MARKET_CONTROL_HEIGHT = 28;
const selectSx = {
  ...metaPeriodSelectSx,
  height: MARKET_CONTROL_HEIGHT,
  flexShrink: 0,
  '& .MuiSelect-select': { ...metaPeriodSelectSx['& .MuiSelect-select'], py: 0, display: 'flex', alignItems: 'center', height: '100%' },
};
// Envoltorio de Tooltip: inline-flex para no sumar la línea de texto (subía el control).
const tooltipWrapSx = { display: 'inline-flex', alignItems: 'center' };
const filterLabelSx = { ...marketSmallSx, mr: 0.75 };
const mapButtonSx = {
  ...marketOutlineButtonSx,
  bgcolor: colors.primary,
  borderColor: colors.primary,
  color: '#FFFFFF',
  gap: 0.75,
  '&:hover': { bgcolor: colors.primaryHover || colors.primary },
  '&.Mui-disabled': { bgcolor: 'transparent', color: colors.disabledOnDarkText, borderColor: 'rgba(255,255,255,0.18)' },
};
// Fila fija pestañas + filtros (mismo alto: 40 px).
const toolbarSx = {
  display: 'flex', flexWrap: 'wrap-reverse', alignItems: 'center', columnGap: 2.5, rowGap: 1,
  borderBottom: '1px solid #3A3A3A',
};
const filtersSx = {
  display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 2, ml: 'auto', minHeight: MARKET_TOOLBAR_HEIGHT,
};
const tabSx = (active, disabled) => ({
  height: MARKET_TOOLBAR_HEIGHT,
  display: 'inline-flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  fontFamily: MARKET_FONT,
  fontSize: '13px',
  fontWeight: 600,
  color: disabled ? colors.disabledOnDarkText : active ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
  borderBottom: `2px solid ${active ? colors.primary : 'transparent'}`,
  cursor: disabled ? 'default' : 'pointer',
  userSelect: 'none',
});

function FilterSelect({ label, value, options, onChange, disabled = false, testId }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Typography component="span" sx={filterLabelSx}>{label}</Typography>
      <Select
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        inputProps={{ 'aria-label': label }}
        MenuProps={leadsSelectMenuProps}
        sx={{
          ...selectSx,
          '&.Mui-disabled .MuiSelect-select': { color: colors.disabledOnDarkText, WebkitTextFillColor: colors.disabledOnDarkText },
        }}
        data-testid={testId}
      >
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </Box>
  );
}

const describePhotos = (c) => (Array.isArray(c?.photos) ? c.photos : []);

/** Subtítulo: «Depto 3B · Departamento · 1 dorm · 60 m² · Nueva Córdoba». */
export function marketUnitSubtitle(unit, unitLabel = '') {
  if (!unit) return unitLabel || '';
  const beds = unit.attributes?.bedrooms;
  return [
    unitLabel,
    unit.terrioTypeLabel,
    beds !== null && beds !== undefined ? (beds === 0 ? 'Monoambiente' : `${beds} dorm`) : '',
    unit.surfaceM2 !== null ? `${formatMarketNumber(unit.surfaceM2)} m²` : '',
    unit.barrio,
  ].filter(Boolean).join(' · ');
}

/**
 * Modal «Comparación de mercado» (T2): mismo chrome y tamaño que los modales de Meta
 * (`MetaSubviewDialog`), alto fijo. Filtros y pestañas fijos arriba; solo scrollea el cuerpo.
 * Con los filtros por defecto reusa la respuesta en caché de la tarjeta; cambiar un filtro
 * pide de nuevo (skeleton del mismo alto) y queda en caché. Ver docs/maps/market-comparison.md.
 *
 * Comparables (T3): lista (lo único que scrollea), fotos en un carrusel dentro del modal y
 * «◎ Ver en el mapa» (`onShowOnMap({ radiusM, months, operation })`: cierra el modal y pasa al
 * modo Comparables del mapa principal). Sin `onShowOnMap` (p. ej. sin ubicación) no hay botón.
 *
 * @param {{ itemKind: string, recordId: string, ownerUserId?: string|null, revision?: string|number,
 *   unitLabel?: string, onEdit?: () => void, onClose: () => void,
 *   onShowOnMap?: (view: { radiusM: number|null, months: number|null, operation: string|null }) => void,
 *   initialRadiusM?: number|null, initialMonths?: number|null, initialOperation?: string|null,
 *   initialTab?: 'resumen'|'comparables' }} props
 */
export default function MarketComparisonDialog({
  itemKind,
  recordId,
  ownerUserId = null,
  revision = '',
  unitLabel = '',
  onEdit = undefined,
  onClose,
  onShowOnMap = undefined,
  initialRadiusM = null,
  initialMonths = null,
  initialOperation = null,
  initialTab = 'resumen',
}) {
  // `null` = por defecto (misma clave de caché que la tarjeta).
  const [radiusM, setRadiusM] = useState(initialRadiusM === MARKET_DEFAULT_RADIUS_M ? null : initialRadiusM);
  const [months, setMonths] = useState(initialMonths === MARKET_DEFAULT_MONTHS ? null : initialMonths);
  const [viewOperation, setViewOperation] = useState(initialOperation);
  const [tab, setTab] = useState(initialTab === 'comparables' ? 'comparables' : 'resumen');
  const [photos, setPhotos] = useState(null);
  // Resumen y Comparables scrollean en el DialogContent: scrollbar oscura y fade (ui-scroll-edge-fade).
  const { scrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();
  const filters = useMemo(() => ({ radiusM: radiusM ?? undefined, months: months ?? undefined }), [radiusM, months]);
  const { status, data, reload } = useMarketComparison({ itemKind, recordId, ownerUserId, revision, filters });
  const [lastData, setLastData] = useState(null);

  useEffect(() => {
    if (status === 'ready' && data) setLastData(data);
  }, [status, data]);
  // Cambiar de pestaña, de fotos o de estado reemplaza el contenido: se vuelve a medir el fade.
  useEffect(() => { refreshScrollEdges(); }, [tab, status, photos, refreshScrollEdges]);
  // Con muestra baja la pestaña queda en Comparables (si después llega a 5, sigue ahí).
  useEffect(() => {
    if (status === 'ready' && data && pickMarketBlock(data)?.lowSample) setTab('comparables');
  }, [status, data]);
  // 401 / 403 / 404 con el modal abierto: no hay nada que mostrar.
  useEffect(() => {
    if (status === 'hidden') onClose();
  }, [status, onClose]);

  const shown = status === 'ready' ? data : null;
  // Operación: solo con dos bloques (unidad sin operación): elige cuál se ve, sin pedir de nuevo.
  const blocksSource = shown || lastData;
  const operations = (blocksSource?.blocks || []).map((b) => b.operation).filter(Boolean);
  const showOperation = operations.length > 1;
  const block = shown
    ? (shown.blocks.find((b) => showOperation && b.operation === viewOperation) || pickMarketBlock(shown))
    : null;
  const activeOperation = block?.operation
    || (blocksSource ? pickMarketBlock(blocksSource)?.operation : null)
    || null;
  const isRent = activeOperation === 'alquiler';
  const monthsValue = isRent ? MARKET_RENT_MAX_MONTHS : (months ?? MARKET_DEFAULT_MONTHS);
  const radiusValue = radiusM ?? MARKET_DEFAULT_RADIUS_M;
  const subtitle = marketUnitSubtitle((shown || lastData)?.unit, unitLabel);
  const filtersLabel = [
    activeOperation ? (MARKET_OPERATION_LABELS[activeOperation] || '') : '',
    formatMarketRadius(radiusValue),
    monthsValue ? `${monthsValue} meses` : '',
  ].filter(Boolean).join(' · ');
  const printable = status === 'ready' && shown && block
    ? { data: shown, block, subtitle, filtersLabel }
    : null;
  const { print, printNode } = useMarketReportPrint(printable);
  const comparablesCount = block?.comparables?.length || 0;
  // Muestra baja (1 a 4, `low_sample`): no hay Resumen (sin mediana); se ve solo Comparables,
  // con un aviso único arriba de la lista. Mientras recarga por un filtro se mantiene lo último.
  const lowSampleBlock = block || (lastData ? pickMarketBlock(lastData) : null);
  const lowSample = Boolean(lowSampleBlock?.lowSample);
  const lowSampleTexts = lowSample
    ? describeLowSample(lowSampleBlock.comparables.length, lowSampleBlock.search?.radiusM, blocksSource?.unit)
    : null;
  const activeTab = lowSample ? 'comparables' : tab;

  const onRadius = (v) => setRadiusM(Number(v) === MARKET_DEFAULT_RADIUS_M ? null : Number(v));
  const onMonths = (v) => setMonths(Number(v) === MARKET_DEFAULT_MONTHS ? null : Number(v));
  const handleEdit = onEdit ? () => { onClose(); onEdit(); } : undefined;
  const comparables = block?.comparables || [];
  const withPin = comparables.filter((c) => c.pin).length;
  const handleShowOnMap = onShowOnMap ? () => {
    onClose();
    onShowOnMap({ radiusM, months, operation: block?.operation || null });
  } : undefined;

  let body;
  if (status === 'loading' || status === 'hidden') {
    body = <MarketSummarySkeleton />;
  } else if (status === 'error' || !block) {
    body = (
      <MarketNotice
        testId="market-report-error"
        title="No pudimos cargar la comparación"
        text="Probá de nuevo en un rato."
        actionLabel="Reintentar"
        onAction={reload}
      />
    );
  } else if (activeTab === 'comparables') {
    const list = (
      <MarketComparablesList
        block={block}
        centerChip
        onOpenPhotos={(c, index) => setPhotos({ list: describePhotos(c), index })}
      />
    );
    body = comparables.length ? (
      block.lowSample ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <MarketNotice testId="market-low-sample-notice" text={lowSampleTexts?.noticeText} />
          {list}
        </Box>
      ) : list
    ) : (
      <MarketNotice
        testId="market-comparables-empty"
        title="Sin comparables para mostrar"
        text="Probá con un radio o un período más amplio."
      />
    );
  } else {
    body = <MarketSummary data={shown} block={block} onEdit={handleEdit} />;
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          ...appModalPaperSx,
          width: 'min(720px, 96vw)',
          maxWidth: '720px',
          height: { xs: '92vh', sm: 'min(860px, 92vh)' },
          maxHeight: '92vh',
        },
        'data-testid': 'market-report-dialog',
      }}
      aria-labelledby="market-report-dialog-title"
    >
      <DialogTitle id="market-report-dialog-title" component="div" sx={appModalTitleBarSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ ...appModalTitleSx, fontSize: '18px' }}>{MARKET_DIALOG_TITLE}</Typography>
          <Typography sx={subtitleSx} noWrap data-testid="market-report-subtitle">{subtitle || '\u00A0'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <MarketReportMenu disabled={!printable} onPrint={print} />
          <IconButton size="small" onClick={onClose} aria-label="Cerrar" sx={appModalCloseButtonSx}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />

      {/* Fijo: una fila con las pestañas (izquierda) y los filtros (derecha). La línea «800 m ·
          últimos 24 meses» se sacó (repetía los filtros); la búsqueda ampliada queda en la línea
          final del Resumen. `wrap-reverse`: si no entra (pantalla angosta), los filtros pasan a
          la línea de arriba, a la derecha, sin truncar; las pestañas siguen sobre el divisor. */}
      <Box sx={{ px: '20px', flexShrink: 0 }}>
        <Box sx={toolbarSx} data-testid="market-report-toolbar">
          <Box sx={{ display: 'flex', gap: 2.5 }} role="tablist">
            {[['resumen', 'Resumen'], ['comparables', comparablesCount ? `Comparables · ${comparablesCount}` : 'Comparables']].map(([id, label]) => {
              const disabled = id === 'resumen' && lowSample;
              return (
                <Tooltip key={id} title={disabled ? 'Pocos casos para un resumen' : ''} placement="bottom" arrow>
                  <Box component="span" sx={tooltipWrapSx}>
                    <Box
                      component="button"
                      type="button"
                      role="tab"
                      aria-selected={activeTab === id ? 'true' : 'false'}
                      aria-disabled={disabled ? 'true' : undefined}
                      onClick={disabled ? undefined : () => { setTab(id); setPhotos(null); }}
                      sx={{ ...tabSx(activeTab === id, disabled), bgcolor: 'transparent', borderTop: 0, borderLeft: 0, borderRight: 0, px: 0 }}
                      data-testid={`market-tab-${id}`}
                    >
                      {label}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Box sx={filtersSx} data-testid="market-report-filters">
            {showOperation ? (
              <FilterSelect
                label="Operación"
                value={activeOperation || operations[0]}
                options={operations.map((op) => ({ value: op, label: MARKET_OPERATION_LABELS[op] || op }))}
                onChange={setViewOperation}
                testId="market-filter-operation"
              />
            ) : null}
            <FilterSelect
              label="Radio"
              value={radiusValue}
              options={MARKET_RADIUS_OPTIONS.map((r) => ({ value: r, label: formatMarketRadius(r) }))}
              onChange={onRadius}
              testId="market-filter-radius"
            />
            <Tooltip title={isRent ? 'En alquiler se comparan los últimos 12 meses.' : ''} placement="top" arrow>
              <Box component="span" sx={tooltipWrapSx}>
                <FilterSelect
                  label="Período"
                  value={monthsValue}
                  options={(isRent ? [MARKET_RENT_MAX_MONTHS] : MARKET_MONTHS_OPTIONS).map((m) => ({ value: m, label: `${m} meses` }))}
                  onChange={onMonths}
                  disabled={isRent}
                  testId="market-filter-months"
                />
              </Box>
            </Tooltip>
          </Box>
        </Box>
        {activeTab === 'comparables' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, height: MARKET_TOOLBAR_HEIGHT }} data-testid="market-comparables-head">
            <Typography sx={marketSmallSx} noWrap>
              {status === 'ready' && block
                ? `${comparables.length}${block.nComparables && block.nComparables > comparables.length ? ` de ${block.nComparables}` : ''} · más parecidas primero`
                : ''}
            </Typography>
            {handleShowOnMap ? (
              <Tooltip title={status === 'ready' && block && !withPin ? 'Ninguno tiene ubicación' : ''} placement="top" arrow>
                <Box component="span" sx={tooltipWrapSx}>
                  <Button
                    size="small"
                    onClick={handleShowOnMap}
                    disabled={!(status === 'ready' && block && withPin)}
                    sx={mapButtonSx}
                    data-testid="market-show-on-map"
                  >
                    <GpsFixedIcon sx={{ fontSize: 15 }} aria-hidden />
                    Ver en el mapa
                  </Button>
                </Box>
              </Tooltip>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {/* Única zona con scroll (el carrusel de fotos la cubre sin abrir otro diálogo). */}
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DialogContent
          ref={scrollRef}
          sx={{
            px: '20px', pt: activeTab === 'comparables' ? 0 : 2, pb: 2, flex: 1, minHeight: 0, overflowY: 'auto',
            ...DOCK_SCROLLBAR_SX,
            scrollbarGutter: 'stable',
            ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
          }}
          data-testid="market-report-body"
        >
          {body}
        </DialogContent>
        {photos && photos.list.length ? (
          <MarketPhotoViewer
            photos={photos.list}
            index={photos.index}
            onIndexChange={(index) => setPhotos((p) => ({ ...p, index }))}
            onClose={() => setPhotos(null)}
          />
        ) : null}
      </Box>
      {printNode}
    </Dialog>
  );
}
